import { useState } from "react";
import { Button, cn, Input, Textarea } from "@carry/ui";
import { BottomBar } from "../components/BottomBar";
import { Header } from "../components/Header";
import { ReportCard, ReportSectionTitle } from "../components/ReportCard";
import { createAlcoholCheck, type DriverFetcher } from "../hooks";
import { enqueueAction } from "../offline-queue";
import { generatePublicId } from "@carry/shared";

export function AlcoholScreen({
  fetcher,
  onBack,
  onDone,
}: {
  fetcher: DriverFetcher;
  onBack: () => void;
  /** アルコール検査の記録が完了したときに呼ばれる (日報画面のバッジ更新用) */
  onDone?: () => void;
}) {
  const [measuredValue, setMeasuredValue] = useState("0.00");
  const [result, setResult] = useState<"pass" | "fail">("pass");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const input = {
        idempotency_key: generatePublicId(),
        result,
        measured_value: measuredValue,
        notes: notes || undefined,
      };
      try {
        await createAlcoholCheck(fetcher, input);
      } catch (e) {
        enqueueAction({
          kind: "create-alcohol-check",
          path: "/api/v1/driver/alcohol-checks",
          method: "POST",
          body: input,
        });
        throw e;
      }
      setSubmitted(true);
      onDone?.();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header title="アルコール検査" sub="始業時に実施" showBack onBack={onBack} />
      <div className="px-3 py-3.5">
        <ReportSectionTitle label="測定値 (mg/L)" />
        <ReportCard>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="9.99"
            value={measuredValue}
            onChange={(e) => {
              setMeasuredValue(e.target.value);
              const n = Number(e.target.value);
              if (Number.isFinite(n) && n > 0) setResult("fail");
              else setResult("pass");
            }}
            className="num h-auto py-3 text-center text-[length:var(--fs-lg)] font-semibold"
          />
        </ReportCard>
        <ReportSectionTitle label="判定" className="mt-3.5" />
        <ReportCard pad={10}>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setResult("pass")}
              className={cn(
                "flex-1 cursor-pointer rounded-lg py-3 text-[length:var(--fs-md)] font-semibold",
                result === "pass"
                  ? "border-2 border-[var(--ok)] bg-[var(--st-done-bg)] text-[var(--st-done-ink)]"
                  : "border border-[var(--rule-soft)] bg-[var(--bg-raised)] text-[var(--ink-3)]",
              )}
            >
              PASS
            </button>
            <button
              type="button"
              onClick={() => setResult("fail")}
              className={cn(
                "flex-1 cursor-pointer rounded-lg py-3 text-[length:var(--fs-md)] font-semibold",
                result === "fail"
                  ? "border-2 border-[var(--danger)] bg-[var(--st-cancel-bg)] text-[var(--danger)]"
                  : "border border-[var(--rule-soft)] bg-[var(--bg-raised)] text-[var(--ink-3)]",
              )}
            >
              FAIL
            </button>
          </div>
        </ReportCard>
        <ReportSectionTitle label="備考" hint="任意" className="mt-3.5" />
        <ReportCard>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="検査者・機器型番等"
            className="min-h-[60px] resize-y border-0 bg-transparent p-0 text-base text-[var(--ink-2)] shadow-none focus-visible:ring-0"
          />
        </ReportCard>
        {submitError && (
          <div className="mt-2.5 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            ⚠ {submitError}
          </div>
        )}
        {submitted && (
          <div className="mt-2.5 rounded-lg bg-[var(--st-done-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-done-ink)]">
            ✓ アルコール検査を記録しました
          </div>
        )}
      </div>
      <BottomBar>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="h-auto min-h-[var(--row-h)] px-3.5 py-3 text-[length:var(--fs-sm)] text-[var(--ink-3)]"
        >
          戻る
        </Button>
        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={submitting || submitted}
          className={cn(
            "h-auto min-h-[var(--row-h)] flex-1 bg-[var(--ok)] px-4 py-3 text-[length:var(--fs-md)] font-semibold text-white hover:bg-[var(--ok)]/90",
            submitting && "cursor-wait opacity-70",
          )}
        >
          {submitted ? "送信済" : submitting ? "送信中…" : "検査を記録"}
        </Button>
      </BottomBar>
    </div>
  );
}
